import React, { useMemo, useState } from 'react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { Slider } from '@/components/ui/slider';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { fmtDate, fmtPct } from '@/lib/format';

/**
 * Before/After satellite viewer with:
 *  - side-by-side swipe (default)
 *  - opacity slider for change mask overlay (yellow bboxes + red mask overlay)
 *  - toggle to show overlay
 */
export function BeforeAfterViewer({ before, after, overlayB64, meta, detection }) {
  const [showOverlay, setShowOverlay] = useState(true);
  const [opacity, setOpacity] = useState(70);

  const beforeSrc = useMemo(() => (before?.startsWith('data:') ? before : `data:image/jpeg;base64,${before}`), [before]);
  const afterSrc = useMemo(() => (after?.startsWith('data:') ? after : `data:image/jpeg;base64,${after}`), [after]);
  const overlaySrc = useMemo(() => overlayB64 ? `data:image/jpeg;base64,${overlayB64}` : null, [overlayB64]);

  return (
    <Card className="overflow-hidden" data-testid="before-after-viewer">
      <div className="relative bg-black">
        <ReactCompareSlider
          data-testid="before-after-swipe-slider"
          itemOne={<ReactCompareSliderImage src={beforeSrc} alt="before" />}
          itemTwo={<ReactCompareSliderImage src={afterSrc} alt="after" />}
          style={{ height: 460 }}
        />
        {showOverlay && overlaySrc && (
          <img
            src={overlaySrc}
            alt="change overlay"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: opacity / 100, mixBlendMode: 'screen' }}
          />
        )}
        <div className="absolute top-2 left-2 flex gap-2">
          <Badge variant="secondary" className="bg-black/70 text-white border-none">BEFORE</Badge>
        </div>
        <div className="absolute top-2 right-2 flex gap-2">
          <Badge variant="secondary" className="bg-black/70 text-white border-none">AFTER</Badge>
        </div>
      </div>

      <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-center border-t border-border">
        <div className="flex items-center gap-2">
          <Switch id="toggle-overlay" checked={showOverlay} onCheckedChange={setShowOverlay} data-testid="toggle-show-overlay" />
          <Label htmlFor="toggle-overlay" className="text-xs">Show change overlay</Label>
        </div>
        <div className="col-span-2 flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-24">Overlay opacity</span>
          <Slider
            value={[opacity]}
            onValueChange={([v]) => setOpacity(v)}
            min={0}
            max={100}
            step={5}
            data-testid="change-mask-opacity-slider"
            className="flex-1"
          />
          <span className="text-xs font-mono tabular-nums w-8 text-right">{opacity}%</span>
        </div>
      </div>
      {meta && (
        <div className="px-3 pb-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-muted-foreground border-t border-border pt-2">
          <div><span className="font-medium text-foreground">Baseline:</span> {fmtDate(meta.baseline)}</div>
          <div><span className="font-medium text-foreground">Observation:</span> {fmtDate(meta.after)}</div>
          <div><span className="font-medium text-foreground">Source:</span> {meta.source || '—'}</div>
          <div><span className="font-medium text-foreground">Resolution:</span> {meta.resolution ? `${meta.resolution} m` : '—'}</div>
          {detection && (
            <>
              <div><span className="font-medium text-foreground">Change area:</span> <span className="font-mono">{fmtPct(detection.affected_area_pct)}</span></div>
              <div><span className="font-medium text-foreground">Regions:</span> <span className="font-mono">{detection.num_regions}</span></div>
              <div><span className="font-medium text-foreground">Confidence:</span> <span className="font-mono">{Math.round((detection.llm_confidence || 0) * 100)}%</span></div>
              <div><span className="font-medium text-foreground">Risk:</span> <span className="font-mono">{detection.risk_score}</span></div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
